import { useReservationDetail } from '@/features/reservations/hooks/useReservationDetail';
import { allowedActionsForReservation } from '@/features/reservations/hooks/allowedActionsForReservation';
import { useReservationActionHandlers } from '@/features/reservations/hooks/useReservationActionHandlers';
import { useSetReservationLabels } from '@/features/reservations/hooks/useReservationMutations';
import LabelChipRow from '@/features/labels/components/LabelChipRow';
import { safeLabelColor } from '@/features/labels/utils/label-color';
import { useLabels } from '@/features/labels/hooks/useLabels';
import EmptyState from '@/components/EmptyState';
import ReservationActions from '@/features/reservations/components/ReservationActions';
import ReservationBadge from '@/features/reservations/components/ReservationBadge';
import ReservationConflictNotice from '@/features/reservations/components/ReservationConflictNotice';
import { useSpecialRequestLabels } from '@/features/reservations/special-request-labels';
import { useT } from '@/i18n';
import { copyPhoneAndPromptCall, handleContactLinkClick } from '@/utils/contactLinks';
import {
  formatDateTime,
  formatDay,
  formatMonth,
  formatTime,
  formatWeekday,
} from '@/utils/datetime';
import type { DisplayStatus } from '@/utils/status';
import { ArrivalStatus, ReservationStatus } from '@restaurant/types';
import {
  Block,
  BlockTitle,
  Card,
  CardContent,
  List,
  ListItem,
  Popover,
  Preloader,
  f7,
} from 'framework7-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import EditReservationPopup from '@/features/reservations/components/EditReservationPopup';

/**
 * Body-only reservation detail. Lives in the workspace's right inspector pane
 * on wide layouts, and inside a PushSheet modal on phones - so it doesn't
 * render its own Navbar/Page.
 *
 * Reach-the-guest pills (Call / Message / Email) and the contextual
 * "Change status" pill share one row directly under the hero card. Status
 * changes open the contextual popover - same UI the badge tap used to show.
 */
/** Imperative handle so the host chrome (the phone push page's navbar) can open
 *  the edit form, even though the edit popup + loaded reservation live in here. */
export interface ReservationInspectorHandle {
  openEdit: () => void;
}

// f7 glyph per reservation audit event type - keeps the timeline scannable.
const RESERVATION_EVENT_ICON: Record<string, string> = {
  CREATED: 'plus_circle',
  CONFIRMED: 'checkmark_circle',
  REJECTED: 'xmark_circle',
  CANCELLED_BY_GUEST: 'xmark_circle',
  CANCELLED_BY_STAFF: 'xmark_circle',
  MODIFIED: 'pencil_circle',
  REACTIVATED: 'arrow_clockwise_circle',
  AUTO_ARRIVED: 'person_crop_circle_badge_checkmark',
  AUTO_COMPLETED: 'checkmark_seal',
  NO_SHOW_ALERTED: 'person_crop_circle_badge_xmark',
  ARRIVAL_MARKED: 'person_crop_circle_badge_checkmark',
  STATUS_CHANGED: 'slider_horizontal_3',
};

// A timeline row's title prefers the concrete state the event landed on over
// the generic event label, so the log reads "Arrived → Seated → In progress →
// Completed" instead of repeated "Arrival updated" / "Status changed". These
// map the resulting arrival/reservation status onto a t.reservations.displayStatus
// key so both share one localized label set.
const ARRIVAL_DISPLAY_KEY: Record<ArrivalStatus, DisplayStatus> = {
  [ArrivalStatus.WAITING]: 'waiting',
  [ArrivalStatus.ARRIVED]: 'arrived',
  [ArrivalStatus.SEATED]: 'seated',
  [ArrivalStatus.IN_PROGRESS]: 'in-progress',
  [ArrivalStatus.COMPLETED]: 'completed',
  [ArrivalStatus.NO_SHOW]: 'no-show',
  [ArrivalStatus.RESCHEDULED]: 'rescheduled',
};
const STATUS_DISPLAY_KEY: Record<ReservationStatus, DisplayStatus> = {
  [ReservationStatus.PENDING]: 'pending',
  [ReservationStatus.CONFIRMED]: 'confirmed',
  [ReservationStatus.REJECTED]: 'rejected',
  [ReservationStatus.CANCELLED]: 'cancelled',
};

interface Props {
  reservationId: string | null;
  /** When provided, renders a close button (used by the docked workspace pane;
   *  the pushed phone page leaves it out and relies on the navbar back link). */
  onClose?: () => void;
}

const ReservationInspector = forwardRef<ReservationInspectorHandle, Props>(
  function ReservationInspector({ reservationId, onClose }, ref) {
    const t = useT();
    const { occasionLabel, seatingLabel } = useSpecialRequestLabels();
    const sourceLabels: Record<string, string> = t.reservations.source;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const popoverRef = useRef<any>(null);
    const labelPopoverRef = useRef<any>(null);
    const [editPopupOpen, setEditPopupOpen] = useState(false);
    useImperativeHandle(ref, () => ({ openEdit: () => setEditPopupOpen(true) }), []);
    const { data: reservation, isLoading, error } = useReservationDetail(reservationId ?? '');
    const {
      handleConfirm,
      handleReactivate,
      handleReject,
      handleCancel,
      handleMarkArrived,
      handleMarkSeated,
      handleMarkInProgress,
      handleMarkCompleted,
      handleMarkNoShow,
      handleMarkNotStarted,
      handleReschedule,
    } = useReservationActionHandlers();
    const allowedActions = allowedActionsForReservation(reservation ?? null);

    const { data: labelOptions = [] } = useLabels();
    const setLabels = useSetReservationLabels(reservationId ?? '');
    const assignedLabels = reservation?.labels ?? [];
    const selectedLabelIds = assignedLabels.map((l) => l.id);

    const toggleLabel = (labelId: string) => {
      const next = selectedLabelIds.includes(labelId)
        ? assignedLabels.filter((l) => l.id !== labelId)
        : [...assignedLabels, ...labelOptions.filter((l) => l.id === labelId)];
      setLabels.mutate(next, {
        onError: () =>
          f7.toast.show({
            text: t.labels.couldNotAssign,
            closeTimeout: 2000,
            position: 'bottom',
          }),
      });
    };

    const openLabelSettings = (e: React.MouseEvent) => {
      e.preventDefault();
      f7.views.main.router.navigate('/settings/labels');
    };

    const openStatusPopover = (e: React.MouseEvent) => {
      const target = e.currentTarget as HTMLElement;
      requestAnimationFrame(() => {
        if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, target);
      });
    };

    // Close affordance for the docked pane. Sticky top-right so it stays reachable
    // while the detail content scrolls underneath. Omitted on the pushed phone
    // page (no onClose), which has its own navbar back link.
    const closeButton = onClose ? (
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '4px 4px 0',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t.reservations.detail.aria.close}
          className="no-ripple reservation-close-btn"
        >
          <i className="f7-icons" style={{ fontSize: 18 }}>
            xmark
          </i>
        </button>
      </div>
    ) : null;

    if (!reservationId) {
      return (
        <>
          {closeButton}
          <EmptyState
            icon="sidebar_right"
            title={t.reservations.detail.none}
            description={t.reservations.detail.noneDesc}
          />
        </>
      );
    }
    if (isLoading) {
      return (
        <>
          {closeButton}
          <Block style={{ textAlign: 'center', paddingTop: 48 }}>
            <Preloader size={32} />
          </Block>
        </>
      );
    }
    if (error || !reservation) {
      return (
        <>
          {closeButton}
          <EmptyState icon="exclamationmark_triangle" title={t.reservations.detail.notFound} />
        </>
      );
    }

    return (
      <>
        {/* Docked-pane header (wide layouts only): trailing Edit + Close buttons,
          sticky top-right. The pushed phone page omits onClose and instead hosts
          the Edit button in its own navbar (see ReservationDetailPage). */}
        {onClose && (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '4px 4px 0',
            }}
          >
            <button
              type="button"
              onClick={() => setEditPopupOpen(true)}
              aria-label={t.reservations.detail.aria.edit}
              className="no-ripple reservation-close-btn"
            >
              <i className="f7-icons" style={{ fontSize: 18 }}>
                pencil
              </i>
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t.reservations.detail.aria.close}
              className="no-ripple reservation-close-btn"
            >
              <i className="f7-icons" style={{ fontSize: 18 }}>
                xmark
              </i>
            </button>
          </div>
        )}
        {/* Hero card - big time/date block on the left, guest on the right. */}
        <Card className="rsv-hero">
          <CardContent>
            <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minWidth: 64,
                  padding: '4px 0',
                  borderRight: '1px solid var(--f7-list-item-border-color, #e5e5ea)',
                  paddingRight: 16,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 700,
                      lineHeight: 1,
                      color: 'var(--f7-theme-color)',
                    }}
                  >
                    {formatTime(reservation.time)}
                  </span>
                  <span style={{ fontSize: 11, color: '#8e8e93', marginTop: 4 }}>
                    {reservation.duration} {t.reservations.detail.minutesShort}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: 12,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#8e8e93' }}>
                    {formatWeekday(reservation.time)} {formatDay(reservation.time)}
                  </span>
                  <span style={{ fontSize: 11, color: '#8e8e93' }}>
                    {formatMonth(reservation.time)}
                  </span>
                </div>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {reservation.guest.name}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#8e8e93',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <i className="f7-icons" style={{ fontSize: 14 }}>
                    person_2
                  </i>
                  {reservation.partySize}{' '}
                  {reservation.partySize === 1 ? t.reservations.guest : t.reservations.guests}
                </div>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: '#8e8e93' }}>
                  #{reservation.confirmationCode}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <ReservationConflictNotice reservation={reservation} />

        {/* Reach-the-guest pills + status pill, all in one row directly under
          the hero. "Change status" matches the same pill style as the contact
          actions and opens the contextual transition popover. */}
        <div className="rsv-quick-actions">
          {reservation.guest.phone && (
            <a
              className="rsv-quick-action external"
              href={`tel:${reservation.guest.phone}`}
              aria-label={t.reservations.detail.aria.call}
              onClick={(e) =>
                handleContactLinkClick(
                  e,
                  reservation.guest.phone!,
                  t.reservations.detail.phoneCopied,
                )
              }
            >
              <i className="f7-icons">phone_fill</i>
              <span>{t.reservations.detail.call}</span>
            </a>
          )}
          {reservation.guest.phone && (
            <a
              className="rsv-quick-action external"
              href={`sms:${reservation.guest.phone}`}
              aria-label={t.reservations.detail.aria.message}
              onClick={(e) =>
                handleContactLinkClick(
                  e,
                  reservation.guest.phone!,
                  t.reservations.detail.phoneCopied,
                )
              }
            >
              <i className="f7-icons">chat_bubble_fill</i>
              <span>{t.reservations.detail.message}</span>
            </a>
          )}
        </div>

        {/* Change-status row - uses the same ReservationBadge trigger as the
          list card. Tap the badge to open the contextual transition popover.
          Same control outside (list) and inside (detail) so staff only have
          to memorize one pattern. */}
        <List strongIos insetIos>
          <ListItem title={t.reservations.detail.status}>
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              slider_horizontal_3
            </i>
            <div slot="after">
              <ReservationBadge
                status={reservation.status}
                arrivalStatus={reservation.arrivalStatus}
                reservationTime={reservation.time}
                onClick={openStatusPopover}
              />
            </div>
          </ListItem>
        </List>

        {reservation.specialRequest &&
          (reservation.specialRequest.occasion.length > 0 ||
            reservation.specialRequest.seatingPreference.length > 0) && (
            <>
              <BlockTitle>{t.reservations.detail.specialRequests}</BlockTitle>
              <List strongIos insetIos dividersIos className="rsv-special-requests">
                {reservation.specialRequest.occasion.length > 0 && (
                  <ListItem
                    title={t.reservations.detail.occasion}
                    after={reservation.specialRequest.occasion.map(occasionLabel).join(', ')}
                  >
                    <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                      gift
                    </i>
                  </ListItem>
                )}
                {reservation.specialRequest.seatingPreference.length > 0 && (
                  <ListItem
                    title={t.reservations.detail.seating}
                    after={reservation.specialRequest.seatingPreference
                      .map(seatingLabel)
                      .join(', ')}
                  >
                    <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                      square_grid_2x2
                    </i>
                  </ListItem>
                )}
              </List>
            </>
          )}

        <BlockTitle>{t.reservations.detail.guestInfo}</BlockTitle>
        <List strongIos insetIos dividersIos>
          {reservation.guest.phone && (
            <ListItem
              title={`tel: ${reservation.guest.phone}`}
              link
              onClick={() =>
                copyPhoneAndPromptCall(
                  reservation.guest.phone!,
                  t.reservations.detail.phoneCopied,
                  t.reservations.detail.call,
                  t.no,
                )
              }
            >
              <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                phone
              </i>
            </ListItem>
          )}
          {reservation.guest.email && (
            <ListItem
              className="item-after-wrap"
              title={t.reservations.detail.email}
              after={reservation.guest.email}
            >
              <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                envelope
              </i>
            </ListItem>
          )}
          <ListItem
            title={t.reservations.detail.source}
            after={sourceLabels[reservation.source] ?? reservation.source}
          >
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              globe
            </i>
          </ListItem>
        </List>

        {reservation.internalNote && (
          <>
            <BlockTitle>{t.reservations.detail.internalNote}</BlockTitle>
            <List strongIos insetIos className="rsv-note-highlight">
              <ListItem>
                <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                  doc_text
                </i>
                <div slot="title" className="rsv-multiline">
                  {reservation.internalNote}
                </div>
              </ListItem>
            </List>
          </>
        )}

        {reservation.cancellationReason && (
          <>
            <BlockTitle>{t.reservations.detail.cancellationReason}</BlockTitle>
            <List strongIos insetIos>
              <ListItem>
                <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                  xmark_circle
                </i>
                <div slot="title" className="rsv-multiline">
                  {reservation.cancellationReason}
                </div>
              </ListItem>
            </List>
          </>
        )}

        <BlockTitle>{t.reservations.detail.labels}</BlockTitle>
        <Block>
          {labelOptions.length === 0 ? (
            <div style={{ fontSize: 14, color: 'var(--rt-secondary, rgba(60, 60, 67, 0.6))' }}>
              {t.labels.none}{' '}
              <a href="/settings/labels" onClick={openLabelSettings}>
                {t.labels.setUp}
              </a>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                ...(setLabels.isPending ? { opacity: 0.6 } : undefined),
              }}
            >
              <LabelChipRow labels={assignedLabels} max={10} />
              <button
                type="button"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: '1.5px dashed var(--f7-list-item-border-color, #c7c7cc)',
                  background: 'transparent',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onClick={(e) => {
                  if (labelPopoverRef.current?.el)
                    f7.popover.open(labelPopoverRef.current.el, e.currentTarget as HTMLElement);
                }}
              >
                <i className="f7-icons" style={{ fontSize: 16, color: 'var(--f7-theme-color)' }}>
                  plus
                </i>
              </button>
            </div>
          )}
        </Block>
        <Popover ref={labelPopoverRef} className="reservation-label-popover">
          <List>
            {labelOptions.map((label) => (
              <ListItem
                key={label.id}
                checkbox
                checked={selectedLabelIds.includes(label.id)}
                title={label.name}
                onChange={() => toggleLabel(label.id)}
              >
                <span
                  slot="after"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: safeLabelColor(label.color),
                    display: 'inline-block',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Popover>

        <BlockTitle>{t.reservations.detail.timeline}</BlockTitle>
        <List strongIos insetIos dividersIos>
          {/* Always the first row: the booking's creation. Rendered from
              reservation.createdAt so it's present even for bookings made before
              the audit log existed (which have no CREATED event). Real audit
              events append below; a CREATED event (new bookings) is skipped so it
              doesn't duplicate this row. */}
          <ListItem
            title={t.reservations.detail.events.created}
            after={formatDateTime(reservation.createdAt)}
          >
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              plus_circle
            </i>
          </ListItem>
          {/* Append-only audit log (createdAt asc). One row per state change:
              event label + actor-type (never the resolved user/customer name)
              + timestamp, with the reason on a second line when present. */}
          {reservation.events
            .filter((e) => e.type !== 'CREATED')
            .map((e) => (
              <ListItem
                key={e.id}
                title={
                  e.toArrivalStatus
                    ? t.reservations.displayStatus[ARRIVAL_DISPLAY_KEY[e.toArrivalStatus]]
                    : e.toStatus
                      ? t.reservations.displayStatus[STATUS_DISPLAY_KEY[e.toStatus]]
                      : (t.audit.reservationEvent[e.type] ?? e.type)
                }
                after={formatDateTime(e.createdAt)}
                text={
                  e.reason
                    ? `${t.audit.actor[e.actorType]} · ${e.reason}`
                    : t.audit.actor[e.actorType]
                }
              >
                <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                  {RESERVATION_EVENT_ICON[e.type] ?? 'circle'}
                </i>
              </ListItem>
            ))}
        </List>

        <ReservationActions
          ref={popoverRef}
          reservation={reservation}
          allowedActions={allowedActions}
          onClose={() => f7.popover.close()}
          onConfirm={handleConfirm}
          onReactivate={handleReactivate}
          onReject={handleReject}
          onCancel={handleCancel}
          onMarkArrived={handleMarkArrived}
          onMarkSeated={handleMarkSeated}
          onMarkInProgress={handleMarkInProgress}
          onMarkCompleted={handleMarkCompleted}
          onMarkNoShow={handleMarkNoShow}
          onMarkNotStarted={handleMarkNotStarted}
          onReschedule={handleReschedule}
        />

        <EditReservationPopup
          opened={editPopupOpen}
          onClose={() => setEditPopupOpen(false)}
          reservation={reservation}
        />
      </>
    );
  },
);

export default ReservationInspector;
